/**
 * useSortState — reads and writes sort parameters from listSlice.
 * Encapsulates sort param reads/writes to reduce direct store coupling in ListTable.
 *
 * @module hooks/useSortState
 * @see FMPV-MVWC-E002-S003 R-001
 */
import { useCallback } from 'react';
import { useMasterViewStore } from '../store/MasterViewContext';

export interface SortState {
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  setSort: (field: string, direction: 'asc' | 'desc') => void;
}

/**
 * Returns current sort state and a setter from the MasterView store.
 * Components that need sort state should use this hook instead of
 * selecting searchParams directly.
 */
export function useSortState(): SortState {
  const sortBy = useMasterViewStore((s) => s.searchParams.sortBy);
  const sortOrder = useMasterViewStore((s) => s.searchParams.sortOrder);
  const setSearchParams = useMasterViewStore((s) => s.setSearchParams);

  const setSort = useCallback(
    (field: string, direction: 'asc' | 'desc') => {
      setSearchParams({ sortBy: field, sortOrder: direction });
    },
    [setSearchParams],
  );

  return { sortBy, sortOrder, setSort };
}
